import { EnvManager } from '@services/environment/environment.service';
import { logger } from '@services/logger/logger.service';

import {
  DEFAULT_INTERCOM_API_BASE,
  INTERCOM_DISABLED_COOKIE,
  INTERCOM_SCRIPT_ID,
  intercomWidgetUrl,
} from './intercom.constants';
import { IntercomApi } from './intercom.types';

interface IntercomBootOptions {
  userId: string | undefined;
  userJwt: string | undefined;
  locale?: string;
}

/**
 * Feature flags can be re-initialised during a session, which replays the boot chokepoint. The
 * Messenger only needs booting once per session, so we latch until the next shutdown.
 */
let hasBooted = false;

/**
 * Decodes the `exp` claim without verifying the signature — we only want to avoid booting the
 * Messenger with a token Intercom is going to reject. The JWT is minted by Alphonse at
 * authentication and reused for the whole session, so it can outlive a very long one.
 */
function isExpired(jwt: string): boolean {
  try {
    const payload = jwt.split('.')[1];
    if (!payload) return true;

    const { exp } = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));

    return typeof exp === 'number' && exp * 1000 <= Date.now();
  } catch (_error) {
    return true;
  }
}

/**
 * Intercom's official snippet: a queueing stub so calls made before the widget script has
 * loaded are replayed once it does.
 */
function installQueueStub(): void {
  if (typeof window.Intercom === 'function') return;

  const queue: unknown[][] = [];
  const stub: IntercomApi = (...args: unknown[]) => {
    queue.push(args);
  };
  stub.q = queue;
  stub.c = (args: unknown[]) => queue.push(args);

  window.Intercom = stub;
}

function injectWidgetScript(appId: string): void {
  if (document.getElementById(INTERCOM_SCRIPT_ID)) return;

  const script = document.createElement('script');
  script.id = INTERCOM_SCRIPT_ID;
  script.type = 'text/javascript';
  script.async = true;
  script.src = intercomWidgetUrl(appId);
  script.onerror = () => logger.error('LOGIC', 'Intercom widget script failed to load');

  document.head.appendChild(script);
}

function isDisabledByCookie(): boolean {
  return document.cookie
    .split(';')
    .some((cookie) => cookie.trim() === `${INTERCOM_DISABLED_COOKIE}=true`);
}

/**
 * Every domain a cookie could have been scoped to, from the exact host up to the registrable
 * domain, so we clear it wherever Intercom set it.
 */
function cookieDomains(): (string | undefined)[] {
  const parts = window.location.hostname.split('.');

  return [
    undefined, // host-only cookie
    ...parts.map((_, index) => parts.slice(index).join('.')).slice(0, -1),
  ];
}

/**
 * `Intercom('shutdown')` clears the session cookie but leaves `intercom-id-*` behind. On a
 * shared workstation that anonymous identifier survives into the next user's session, and
 * Intercom then merges the previous lead's conversation history into their contact.
 */
function clearCookies(): void {
  const names = document.cookie
    .split(';')
    .map((cookie) => cookie.split('=')[0]?.trim())
    .filter(
      (name): name is string =>
        !!name?.startsWith('intercom-') && name !== INTERCOM_DISABLED_COOKIE,
    );

  names.forEach((name) => {
    cookieDomains().forEach((domain) => {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${
        domain ? `; domain=${domain}` : ''
      }`;
    });
  });
}

export const intercom = {
  /**
   * Boots the Messenger for an authenticated user. The launcher stays hidden: Zendesk is the
   * support chat, Intercom only carries in-app messages, product tours and surveys.
   */
  boot({ userId, userJwt, locale }: IntercomBootOptions): void {
    if (hasBooted) return;

    if (isDisabledByCookie()) {
      logger.info('LOGIC', 'Intercom: disabled by cookie, skipping boot');
      return;
    }

    const appId = EnvManager.getIntercomAppId();

    if (!appId) {
      logger.warn('LOGIC', 'Intercom: no app id configured, skipping boot');
      return;
    }

    if (!userJwt) {
      logger.warn('LOGIC', 'Intercom: no user JWT received, skipping boot');
      return;
    }

    if (isExpired(userJwt)) {
      logger.error('LOGIC', 'Intercom: user JWT is invalid or expired, skipping boot');
      return;
    }

    try {
      window.intercomSettings = {
        app_id: appId,
        api_base: EnvManager.getIntercomApiBase() ?? DEFAULT_INTERCOM_API_BASE,
        hide_default_launcher: true,
        intercom_user_jwt: userJwt,
        language_override: locale,
      };

      installQueueStub();
      injectWidgetScript(appId);

      window.Intercom?.('boot', window.intercomSettings);
      hasBooted = true;

      logger.info('LOGIC', 'Intercom booted', { userId });
    } catch (error) {
      logger.error('LOGIC', 'Intercom: boot failed', error);
    }
  },

  shutdown(): void {
    hasBooted = false;

    try {
      window.Intercom?.('shutdown');
    } catch (error) {
      logger.error('LOGIC', 'Intercom: shutdown failed', error);
    } finally {
      clearCookies();
    }
  },
};
